import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type RevenueShareResource,
  RevenueSharesController_create_v1,
  RevenueSharesController_end_v1,
  type VendorResource,
  VendorsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { DayField } from '../billing-machines/machine-fields';
import { currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';
import { formatRate, milliPercentFrom, rateSchema } from './rate';

/**
 * Opening and closing a partner's revenue-share agreement — both owner-only on
 * the API (`@RequireRole('OWNER')`).
 *
 * **A rate is never edited.** A past day's share has to come from the rate in
 * force that day, so an agreement's rate and first day are fixed, its last day
 * is set once, and a new rate is a new agreement opened after the current one
 * is closed. The API refuses an overlap with `REPORTING_REVENUE_SHARE_OVERLAP`,
 * naming the agreement in the way in `details.agreementId`; the open dialog
 * turns that into a button that closes it.
 *
 * Opening sends no `effectiveTo`: an agreement opens open-ended, and "Close
 * agreement" is the one way to end one. A fixed term is the same two steps.
 */

const PARTNER_REQUIRED = 'Choose the partner paid the share.';
const FROM_REQUIRED = 'Choose the first day the rate applies.';
const TO_REQUIRED = 'Choose the last day the rate applies.';

const openSchema = z.object({
  vendorId: z.string().min(1, PARTNER_REQUIRED),
  // Text until the moment it is sent, and then an integer. See `rate.ts`.
  rate: rateSchema,
  effectiveFrom: z.string().min(1, FROM_REQUIRED),
});

type OpenValues = z.infer<typeof openSchema>;

function ShellDialog({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description !== undefined && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </FormDialogContent>
    </Dialog>
  );
}

export function OpenAgreementDialog({
  outletId,
  outletName,
  agreements,
  onClose,
  onOpened,
  onCloseFirst,
}: {
  outletId: string;
  outletName: string;
  /** This outlet's agreements, to find the one an overlap names. */
  agreements: readonly RevenueShareResource[];
  onClose: () => void;
  onOpened: (agreement: RevenueShareResource) => void;
  onCloseFirst: (agreement: RevenueShareResource) => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  const [vendors, setVendors] = useState<readonly VendorResource[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [inTheWay, setInTheWay] = useState<RevenueShareResource | null>(null);

  const form = useForm({
    resolver: zodResolver(openSchema),
    mode: 'onTouched',
    defaultValues: { vendorId: '', rate: '', effectiveFrom: today },
  });

  const attempt = useRef<WriteAttempt | null>(null);
  const pending = form.formState.isSubmitting;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Active vendors only — the API refuses a retired partner. Read whole: the
    // picker needs every one, and the park's suppliers are few.
    collectPages<VendorResource>((offset, limit) =>
      client.request(VendorsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setVendors(all);
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setFormError(messageForFailure(error));
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  const vendorItems = useMemo(
    () => vendors.map((vendor) => ({ label: vendor.name, value: vendor.id })),
    [vendors],
  );

  const handleSubmit = async (values: OpenValues) => {
    if (pending) return;

    setFormError(null);
    setInTheWay(null);

    const body = {
      vendorId: values.vendorId,
      rateMilliPercent: milliPercentFrom(values.rate),
      effectiveFrom: values.effectiveFrom,
    };
    const sending = attemptFor(attempt.current, JSON.stringify([outletId, body]));
    attempt.current = sending;

    try {
      onOpened(
        await client.request(RevenueSharesController_create_v1, {
          path: { outletId },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      const code = error instanceof ApiError ? error.code : null;

      if (code === 'EXPENSE_VENDOR_UNAVAILABLE') {
        // Retired since the picker loaded.
        form.setError('vendorId', { message: messageForFailure(error) });

        return;
      }

      if (code === 'REPORTING_REVENUE_SHARE_OVERLAP' && error instanceof ApiError) {
        const named = error.details?.['agreementId'];

        setInTheWay(agreements.find((agreement) => agreement.id === named) ?? null);
      }

      setFormError(messageForFailure(error));
    }
  };

  return (
    <ShellDialog title={`Open an agreement at ${outletName}`} onClose={onClose}>
      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="vendorId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Partner
                </FormLabel>
                {/* Not in `FormControl`: the trigger is a button, so it is
                    described by the message by hand, as `OutletField` does. */}
                <Select
                  value={field.value}
                  items={vendorItems}
                  onValueChange={(next) => field.onChange(next ?? '')}
                  disabled={vendorItems.length === 0}
                >
                  <SelectTrigger
                    id="agreement-partner"
                    aria-label="Partner"
                    aria-invalid={fieldState.error !== undefined}
                    aria-describedby={
                      fieldState.error === undefined ? undefined : 'agreement-partner-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose a partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {vendorItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormDescription>
                  A partner is a vendor. Add one under Vendors first.
                </FormDescription>
                <FormMessage id="agreement-partner-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Share of the collection, in percent
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 font-mono numeric`}
                      required
                      inputMode="decimal"
                      placeholder="24.2"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Of the whole day&rsquo;s takings, not a net figure.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <DayField
            control={form.control}
            name="effectiveFrom"
            label="First day it applies"
            idPrefix="agreement-from"
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            {inTheWay !== null && (
              <Button type="button" variant="outline" onClick={() => onCloseFirst(inTheWay)}>
                Close that agreement
              </Button>
            )}
            <Button type="submit" disabled={pending}>
              {pending ? 'Opening…' : 'Open agreement'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </ShellDialog>
  );
}

export function CloseAgreementDialog({
  agreement,
  onClose,
  onClosed,
  onStale,
}: {
  agreement: RevenueShareResource;
  onClose: () => void;
  onClosed: (agreement: RevenueShareResource) => void;
  /** The list behind is out of date: it was closed elsewhere first. */
  onStale: () => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  // Days compare as text: `YYYY-MM-DD` sorts the way the calendar does.
  const schema = useMemo(
    () =>
      z.object({
        effectiveTo: z
          .string()
          .min(1, TO_REQUIRED)
          .refine(
            (day) => day >= agreement.effectiveFrom,
            `The last day cannot be before the first, ${formatBusinessDate(agreement.effectiveFrom)}.`,
          ),
      }),
    [agreement.effectiveFrom],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      effectiveTo: today >= agreement.effectiveFrom ? today : agreement.effectiveFrom,
    },
  });

  const [formError, setFormError] = useState<string | null>(null);
  const attempt = useRef<WriteAttempt | null>(null);
  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ effectiveTo }: { effectiveTo: string }) => {
    if (pending) return;

    setFormError(null);

    const body = { effectiveTo };
    const sending = attemptFor(attempt.current, JSON.stringify([agreement.id, body]));
    attempt.current = sending;

    try {
      onClosed(
        await client.request(RevenueSharesController_end_v1, {
          path: { id: agreement.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'REPORTING_REVENUE_SHARE_ALREADY_ENDED') {
        onStale();
      }

      setFormError(messageForFailure(error));
    }
  };

  return (
    <ShellDialog
      title={`Close the agreement with ${agreement.vendor.name}`}
      description={`${formatRate(agreement.rateMilliPercent)} of the collection from ${formatBusinessDate(agreement.effectiveFrom)}. The last day cannot be moved afterwards.`}
      onClose={onClose}
    >
      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <DayField
            control={form.control}
            name="effectiveTo"
            label="Last day it applies"
            idPrefix="agreement-to"
            description="The share is paid on this day too."
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? 'Closing…' : 'Close agreement'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </ShellDialog>
  );
}
